import React from "react"
import Tooltip from "design_system/core/components/tooltip"
import { LANGUAGE_BY_CODE } from "./languages"

// SVG flags (public domain / CC, sourced from Wikimedia Commons). Celtic regional flags mostly have no
// Unicode emoji (Cornwall, Brittany, Isle of Man), so we ship SVGs rather than emoji for a consistent set.
import gaFlag from "./flags/ga.svg"
import gdFlag from "./flags/gd.svg"
import gvFlag from "./flags/gv.svg"
import cyFlag from "./flags/cy.svg"
import kwFlag from "./flags/kw.svg"
import brFlag from "./flags/br.svg"
import enFlag from "./flags/en.svg"
import { twMerge } from "tailwind-merge"

const FLAGS: Record<string, string> = {
  ga: gaFlag,
  gd: gdFlag,
  gv: gvFlag,
  cy: cyFlag,
  kw: kwFlag,
  br: brFlag,
  en: enFlag,
}

export interface LanguageFlagProps {
  /** ISO language code (ga, gd, gv, cy, kw, br, en). */
  code: string
  /** Flag width in px (height derives at a 3:2 ratio). Default 20. */
  size?: number
  className?: string
}

/** A single language's flag, labelled for screen readers. Renders nothing for an unknown code. */
export const LanguageFlag: React.FC<LanguageFlagProps> = ({
  code,
  size = 20,
  className,
}) => {
  const src = FLAGS[code]
  if (!src) return null
  const language = LANGUAGE_BY_CODE[code]
  const label = language ? `${language.name} flag` : `${code} flag`

  return (
    <Tooltip title={language && `${language.name} / ${language.nativeName}`}>
      <img
        src={src}
        alt={label}
        width={size}
        height={Math.round((size * 2) / 3)}
        className={className}
        style={{ borderRadius: 2, objectFit: "cover", display: "inline-block" }}
      />
    </Tooltip>
  )
}

export interface LanguageFlagsProps {
  /** ISO language codes to display, in order. */
  codes: string[]
  size?: number
  className?: string
}

/** A row of flags for an item's languages. Renders nothing when there are none. */
export const LanguageFlags: React.FC<LanguageFlagsProps> = ({
  codes,
  size,
  className,
}) => {
  if (!codes || codes.length === 0) return null
  return (
    <span className={twMerge("flex items-center gap-4", className)}>
      {codes.map((code) => (
        <LanguageFlag key={code} code={code} size={size} />
      ))}
    </span>
  )
}
